import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { X, Minus, Plus, ShieldCheck } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatINR } from "../lib/api";

const FREE_SHIPPING_THRESHOLD = 2500;

export default function CartDrawer() {
    const { open, setOpen, items, updateQty, removeItem, subtotal, count } =
        useCart();
    const nav = useNavigate();

    const toFreeShipping = Math.max(
        0,
        FREE_SHIPPING_THRESHOLD - subtotal,
    );
    const progress = Math.min(
        100,
        (subtotal / FREE_SHIPPING_THRESHOLD) * 100,
    );
    const hitFree = subtotal >= FREE_SHIPPING_THRESHOLD;

    return (
        <>
            {open && (
                <div
                    data-testid="cart-backdrop"
                    onClick={() => setOpen(false)}
                    className="fixed inset-0 z-[80]"
                    style={{
                        background: "rgba(0,0,0,0.6)",
                        backdropFilter: "blur(6px)",
                    }}
                />
            )}
            <aside
                data-testid="cart-drawer"
                className="fixed top-0 right-0 h-full z-[81] flex flex-col"
                style={{
                    width: "min(460px, 94vw)",
                    background: "var(--vl-surface)",
                    borderLeft: "1px solid var(--vl-border)",
                    transform: open ? "translateX(0)" : "translateX(100%)",
                    transition: "transform 460ms cubic-bezier(0.2,0.7,0.2,1)",
                }}
            >
                <div
                    className="flex items-center justify-between px-6 py-5"
                    style={{ borderBottom: "1px solid var(--vl-border)" }}
                >
                    <div>
                        <div className="overline">Your bag</div>
                        <div
                            className="font-serif text-2xl"
                            style={{ color: "var(--vl-text)" }}
                        >
                            {count}{" "}
                            {count === 1 ? "piece" : "pieces"}
                        </div>
                    </div>
                    <button
                        onClick={() => setOpen(false)}
                        data-testid="cart-close"
                        aria-label="Close cart"
                    >
                        <X
                            size={22}
                            strokeWidth={1.25}
                            color="var(--vl-text)"
                        />
                    </button>
                </div>

                {/* Shipping progress */}
                {items.length > 0 && (
                    <div
                        className="px-6 py-4"
                        style={{
                            borderBottom: "1px solid var(--vl-border)",
                            background: "var(--vl-bg)",
                        }}
                        data-testid="cart-shipping-progress"
                    >
                        <div
                            className="text-[11px] tracking-[0.18em] uppercase mb-3 flex items-center gap-2"
                            style={{
                                color: hitFree
                                    ? "var(--vl-rose)"
                                    : "var(--vl-muted)",
                            }}
                        >
                            <ShieldCheck
                                size={13}
                                strokeWidth={1.25}
                            />
                            {hitFree
                                ? "Complimentary discreet shipping unlocked"
                                : `Add ${formatINR(toFreeShipping)} for free discreet shipping`}
                        </div>
                        <div
                            style={{
                                height: 2,
                                background: "var(--vl-border)",
                                position: "relative",
                                overflow: "hidden",
                            }}
                        >
                            <div
                                style={{
                                    width: `${progress}%`,
                                    height: "100%",
                                    background: "var(--vl-rose)",
                                    transition: "width 420ms ease",
                                }}
                            />
                        </div>
                    </div>
                )}

                <div className="flex-1 overflow-y-auto px-6 py-6">
                    {items.length === 0 && (
                        <div
                            className="text-center mt-16"
                            data-testid="cart-empty"
                        >
                            <p
                                className="font-serif text-2xl"
                                style={{ color: "var(--vl-text)" }}
                            >
                                Your bag is empty.
                            </p>
                            <p
                                className="text-sm mt-3 mb-8"
                                style={{ color: "var(--vl-muted)" }}
                            >
                                Begin with our most-loved pieces.
                            </p>
                            <Link
                                to="/best-sellers"
                                onClick={() => setOpen(false)}
                                className="vl-btn vl-btn-solid"
                                data-testid="cart-empty-shop"
                            >
                                Shop best sellers
                            </Link>
                        </div>
                    )}
                    {items.map((i) => (
                        <div
                            key={i.product_id}
                            className="flex gap-4 py-5"
                            style={{
                                borderBottom: "1px solid var(--vl-border)",
                            }}
                            data-testid={`cart-item-${i.slug}`}
                        >
                            <img
                                src={i.image}
                                alt={i.name}
                                className="w-20 h-24 object-cover"
                                style={{
                                    background: "var(--vl-surface-2)",
                                }}
                            />
                            <div className="flex-1">
                                <Link
                                    to={`/product/${i.slug}`}
                                    onClick={() => setOpen(false)}
                                    className="font-serif text-lg leading-tight"
                                    style={{ color: "var(--vl-text)" }}
                                >
                                    {i.name}
                                </Link>
                                <div
                                    className="text-sm mt-1"
                                    style={{ color: "var(--vl-muted)" }}
                                >
                                    {formatINR(i.price)}
                                </div>
                                <div className="flex items-center gap-3 mt-3">
                                    <button
                                        onClick={() =>
                                            updateQty(
                                                i.product_id,
                                                i.quantity - 1,
                                            )
                                        }
                                        className="w-7 h-7 border flex items-center justify-center"
                                        style={{
                                            borderColor: "var(--vl-border)",
                                        }}
                                        data-testid={`cart-dec-${i.slug}`}
                                    >
                                        <Minus size={12} />
                                    </button>
                                    <span
                                        className="text-sm"
                                        style={{ color: "var(--vl-text)" }}
                                    >
                                        {i.quantity}
                                    </span>
                                    <button
                                        onClick={() =>
                                            updateQty(
                                                i.product_id,
                                                i.quantity + 1,
                                            )
                                        }
                                        className="w-7 h-7 border flex items-center justify-center"
                                        style={{
                                            borderColor: "var(--vl-border)",
                                        }}
                                        data-testid={`cart-inc-${i.slug}`}
                                    >
                                        <Plus size={12} />
                                    </button>
                                    <button
                                        onClick={() =>
                                            removeItem(i.product_id)
                                        }
                                        className="ml-auto text-[11px] uppercase tracking-[0.22em]"
                                        style={{ color: "var(--vl-muted)" }}
                                        data-testid={`cart-remove-${i.slug}`}
                                    >
                                        Remove
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {items.length > 0 && (
                    <div
                        className="px-6 py-6"
                        style={{ borderTop: "1px solid var(--vl-border)" }}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <span className="overline">Subtotal</span>
                            <span
                                className="font-serif text-2xl"
                                style={{ color: "var(--vl-text)" }}
                                data-testid="cart-subtotal"
                            >
                                {formatINR(subtotal)}
                            </span>
                        </div>
                        <button
                            className="vl-btn vl-btn-solid w-full justify-center"
                            data-testid="cart-checkout"
                            onClick={() => {
                                setOpen(false);
                                nav("/checkout");
                            }}
                        >
                            Private checkout
                        </button>
                        <button
                            onClick={() => {
                                setOpen(false);
                                nav("/cart");
                            }}
                            data-testid="cart-view-full"
                            className="w-full text-center text-[11px] tracking-[0.22em] uppercase mt-4"
                            style={{ color: "var(--vl-muted)" }}
                        >
                            View full bag
                        </button>
                        <div
                            className="mt-5 flex items-center justify-center gap-2 text-[10px] tracking-[0.22em] uppercase"
                            style={{ color: "var(--vl-muted)" }}
                        >
                            <ShieldCheck size={11} strokeWidth={1.25} />
                            Unbranded · V. Atelier label · COD available
                        </div>
                    </div>
                )}
            </aside>
        </>
    );
}
